import {
  Blend,
  Contrast,
  Droplets,
  Image as ImageIcon,
  Layers as LayersIcon,
  Palette,
  Pipette,
  Scale,
  Sparkles,
  SunMedium,
  Target,
  Zap,
} from "lucide-react";
import { Switch } from "@/shared/ui/switch";
import { Badge } from "@/shared/ui/badge";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/shared/i18n";
import type { TemplateLayer } from "../types";

interface Props {
  layer: TemplateLayer;
  thumbnail?: string;
  onToggleSlot: () => void;
  onFocus: () => void;
  active?: boolean;
}

const TYPE_ICON = {
  image: ImageIcon,
  slot: Target,
  colorFill: Palette,
  photoFilter: Sparkles,
  blackAndWhite: Contrast,
  brightnessContrast: SunMedium,
  hueSaturation: Droplets,
  vibrance: Zap,
  colorBalance: Scale,
  selectiveColor: Pipette,
  gradientMap: Blend,
  unknown: LayersIcon,
} as const;

export function LayerRow({ layer, thumbnail, onToggleSlot, onFocus, active }: Props) {
  const { t } = useTranslation("psdTemplates");
  const Icon = TYPE_ICON[layer.type] ?? LayersIcon;
  const canBeSlot = layer.type === "image" || layer.type === "slot";
  const isSlot = layer.type === "slot";

  return (
    <div
      onClick={onFocus}
      className={cn(
        "flex items-center gap-3 p-2 rounded-lg border bg-card hover:bg-accent/50 cursor-pointer transition-colors",
        active && "ring-2 ring-primary border-primary",
        isSlot && "border-primary/60",
      )}
    >
      <div className="shrink-0 w-12 h-12 rounded-md bg-muted overflow-hidden flex items-center justify-center border">
        {thumbnail ? (
          <img src={thumbnail} alt={layer.name} className="w-full h-full object-contain" />
        ) : (
          <Icon className="w-5 h-5 text-muted-foreground" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium truncate text-foreground" title={layer.name}>
            {layer.name}
          </p>
          {isSlot && <Badge variant="default">{layer.id}</Badge>}
        </div>
        <p className="text-xs text-muted-foreground truncate">
          {t(`layer.type.${layer.type}`)}
          {layer.group ? ` · ${layer.group}` : ""} · {Math.round(layer.width)}×{Math.round(layer.height)}
          {layer.opacity != null ? ` · ${Math.round(layer.opacity * 100)}%` : ""}
          {layer.blendMode ? ` · ${layer.blendMode}` : ""}
        </p>
      </div>
      <div className="shrink-0 flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{t("layer.slot")}</span>
        <Switch
          checked={isSlot}
          disabled={!canBeSlot}
          onCheckedChange={onToggleSlot}
          onClick={(e) => e.stopPropagation()}
          aria-label={t("layer.slot")}
        />
      </div>
    </div>
  );
}
